import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { RootState } from 'store';
import styles from './LeaderboardTable.module.scss';

const usd = (value: number): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);

const pct = (value: number): string => `${Math.round(value * 100)}%`;

export const LeaderboardTable: React.FC = React.memo(() => {
    const leaderboard = useSelector((state: RootState) => state.salesDashboard.leaderboard);
    const loadingLeaderboard = useSelector((state: RootState) => state.salesDashboard.loadingLeaderboard);

    const [searchText, setSearchText] = useState('');

    const filteredLocations = useMemo(() => {
        const locations = leaderboard?.locations ?? [];
        if (!searchText.trim()) return locations;
        const query = searchText.toLowerCase();
        return locations.filter(loc => loc.name.toLowerCase().includes(query));
    }, [leaderboard, searchText]);

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
                <h3 className={styles.tableTitle}>Leader board (Net sales)</h3>
            </div>

            <div className={styles.searchSection}>
                <SearchBar
                    searchText={searchText}
                    placeholder="Filter locations"
                    onChangeSearchText={setSearchText}
                    className={styles.searchBar}
                />
            </div>

            <div className={styles.tableWrapper}>
                {loadingLeaderboard ? (
                    <div className={styles.loadingRows}>
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className={styles.skeletonRow}
                            >
                                <div className={`${styles.skeletonCell} ${styles.skeletonSm}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonLg}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonMd}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonMd}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonMd}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonMd}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonSm}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonSm}`} />
                                <div className={`${styles.skeletonCell} ${styles.skeletonSm}`} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <>
                        <Table className={styles.table}>
                            <TableHeader>
                                <TableHeaderCell className={styles.rankingColumn}>Ranking</TableHeaderCell>
                                <TableHeaderCell className={styles.nameColumn}>Location name</TableHeaderCell>
                                <TableHeaderCell className={styles.regionColumn}>Region</TableHeaderCell>
                                <TableHeaderCell className={styles.numberColumn}>Net sales</TableHeaderCell>
                                <TableHeaderCell className={styles.numberColumn}>Avg order value</TableHeaderCell>
                                <TableHeaderCell className={styles.numberColumn}>Sales per staff</TableHeaderCell>
                                <TableHeaderCell className={styles.numberColumn}>Active clients</TableHeaderCell>
                                <TableHeaderCell className={styles.numberColumn}>New clients</TableHeaderCell>
                                <TableHeaderCell className={styles.numberColumn}>Retention rate</TableHeaderCell>
                            </TableHeader>

                            <TableBody>
                                {filteredLocations.map(loc => (
                                    <TableRow key={loc.marketplaceId}>
                                        <TableCell className={styles.rankingCell}>{loc.ranking}</TableCell>
                                        <TableCell className={styles.nameCell}>{loc.name}</TableCell>
                                        <TableCell className={styles.regionCell}>{loc.region ?? '—'}</TableCell>
                                        <TableCell className={styles.numberCell}>{usd(loc.netSales)}</TableCell>
                                        <TableCell className={styles.numberCell}>{usd(loc.avgOrderValue)}</TableCell>
                                        <TableCell className={styles.numberCell}>{usd(loc.salesPerStaff)}</TableCell>
                                        <TableCell className={styles.numberCell}>
                                            {loc.activeClients.toLocaleString()}
                                        </TableCell>
                                        <TableCell className={styles.numberCell}>
                                            {loc.newClients.toLocaleString()}
                                        </TableCell>
                                        <TableCell className={styles.numberCell}>{pct(loc.retentionRate)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {filteredLocations.length === 0 && (
                            <div className={styles.noResults}>
                                <p>No locations found{searchText ? ` matching "${searchText}"` : ''}</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
});

LeaderboardTable.displayName = 'LeaderboardTable';
